import axios from 'axios';
import authService from './authService';
import { API_BASE_URL } from './apiConfig';

const API = API_BASE_URL;

class NarratorService {
  getHeaders() {
    return {
      Authorization: `Bearer ${authService.getAccessToken()}`,
      'Content-Type': 'application/json'
    };
  }

  // Profile
  async getProfile() {
    const response = await axios.get(`${API}/narrator/me`, {
      headers: this.getHeaders()
    });
    return response.data;
  }

  async createProfile() {
    const response = await axios.post(`${API}/narrator/create-profile`, {}, {
      headers: this.getHeaders()
    });
    return response.data;
  }

  async updateProfile(profileData) {
    const response = await axios.put(`${API}/narrator/profile`, profileData, {
      headers: this.getHeaders()
    });
    return response.data;
  }

  async getOnboardingStatus() {
    const response = await axios.get(`${API}/narrator/onboarding/status`, {
      headers: this.getHeaders()
    });
    return response.data;
  }

  // Demos
  async getDemos() {
    const response = await axios.get(`${API}/narrator/demos`, {
      headers: this.getHeaders()
    });
    return response.data;
  }

  /**
   * Upload a demo audio file with progress tracking
   * @param {File} file - Audio file (MP3, WAV, M4A)
   * @param {Object} metadata - { title, language, genre_tag, tone_tag }
   * @param {Function} onProgress - Progress callback (0-100)
   * @returns {Promise<Object>} - Upload result
   */
  async uploadDemo(file, metadata = {}, onProgress = null) {
    const formData = new FormData();
    formData.append('file', file);
    
    if (metadata.title) formData.append('title', metadata.title);
    if (metadata.language) formData.append('language', metadata.language);
    if (metadata.genre_tag) formData.append('genre_tag', metadata.genre_tag);
    if (metadata.tone_tag) formData.append('tone_tag', metadata.tone_tag);
    
    const config = {
      headers: {
        Authorization: `Bearer ${authService.getAccessToken()}`
        // DO NOT set Content-Type manually for FormData
      }
    };
    
    // Add progress tracking if callback provided
    if (onProgress) {
      config.onUploadProgress = (progressEvent) => {
        const percentComplete = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total
        );
        onProgress(percentComplete);
      };
    }
    
    const response = await axios.post(
      `${API}/narrator/demos/upload`,
      formData,
      config
    );
    
    return response.data;
  }

  /**
   * Get stream URL for a demo
   */
  getDemoStreamUrl(demoId) {
    return `${API}/narrator/demos/${demoId}/stream`;
  }

  async updateDemo(demoId, demoData) {
    const response = await axios.put(`${API}/narrator/demos/${demoId}`, demoData, {
      headers: this.getHeaders()
    });
    return response.data;
  }

  async deleteDemo(demoId) {
    const response = await axios.delete(`${API}/narrator/demos/${demoId}`, {
      headers: this.getHeaders()
    });
    return response.data;
  }

  async submitDemo(demoId) {
    const response = await axios.post(`${API}/narrator/demos/${demoId}/submit`, {}, {
      headers: this.getHeaders()
    });
    return response.data;
  }

  async setFeaturedDemo(demoId) {
    const response = await axios.post(`${API}/narrator/demos/${demoId}/set-featured`, {}, {
      headers: this.getHeaders()
    });
    return response.data;
  }

  // Projects
  async getProjects() {
    const response = await axios.get(`${API}/narrator/projects`, {
      headers: this.getHeaders()
    });
    return response.data;
  }

  async getProjectWorkspace(projectId) {
    const response = await axios.get(`${API}/narrator/projects/${projectId}`, {
      headers: this.getHeaders()
    });
    return response.data;
  }

  async updateProgress(projectId, recordingPct) {
    const response = await axios.put(
      `${API}/narrator/projects/${projectId}/progress`,
      { recording_pct: recordingPct },
      { headers: this.getHeaders() }
    );
    return response.data;
  }

  async uploadChapterAudio(projectId, chapterId) {
    const response = await axios.post(
      `${API}/narrator/projects/${projectId}/chapters/${chapterId}/upload`,
      {},
      { headers: this.getHeaders() }
    );
    return response.data;
  }

  async submitForQC(projectId) {
    const response = await axios.post(
      `${API}/narrator/projects/${projectId}/submit-for-qc`,
      {},
      { headers: this.getHeaders() }
    );
    return response.data;
  }

  // QC Issues
  async getProjectQC(projectId) {
    const response = await axios.get(`${API}/narrator/projects/${projectId}/qc`, {
      headers: this.getHeaders()
    });
    return response.data;
  }

  async resolveQCIssue(issueId) {
    const response = await axios.put(`${API}/narrator/qc/issues/${issueId}/resolve`, {}, {
      headers: this.getHeaders()
    });
    return response.data;
  }

  // Messages
  async getMessages(projectId = null) {
    const params = projectId ? { project_id: projectId } : {};
    const response = await axios.get(`${API}/narrator/messages`, {
      params,
      headers: this.getHeaders()
    });
    return response.data;
  }

  async sendMessage(messageData) {
    const response = await axios.post(`${API}/narrator/messages`, messageData, {
      headers: this.getHeaders()
    });
    return response.data;
  }

  // Public
  async getPublicProfile(slug) {
    const response = await axios.get(`${API}/public/narrators/${slug}`);
    return response.data;
  }
}

export default new NarratorService();
